import { useEffect, useRef } from "react";
import Lenis from "lenis";
import { createLenis } from "../../lenis";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";
import type { JoinLayoutProps } from "../types";
import { WeddingProgram } from "../WeddingProgram";
import { DEMO_IMAGES, DEMO_VIDEOS, DEMO_FRAME_COUNT } from "../mock-data";
import { useScrollFrameSequence } from "../../useScrollFrameSequence";

gsap.registerPlugin(ScrollTrigger, SplitText);

/**
 * PRISM — 3D Maximalist Showcase
 *
 * Six pinned signature moments. Multi-axis everything. Real 3D depth.
 *
 *  ACT I  – Hero: 3D extruded title, marquees top+bot (opposite directions),
 *           4 satellite cards float from corners with rotateY
 *  ACT II – PINNED 3D CUBE: 6 faces (TITLE/DATE/PLACE/STORY/GALLERY/RSVP),
 *           rotates X+Y as you scroll, satellite cards orbit at 1.5x speed.
 *           Light gradient sweeps across the cube. Cube also scales.
 *  ACT III– VIDEO FRAME SCROLL: scroll = video.currentTime, multi-layered
 *           text crosses screen in opposite directions, image fragments
 *           pop in around the edges.
 *  ACT IV – ORBIT GALLERY: 5 cards arc across viewport, each on its own
 *           parabolic path (x + y + rotate independently).
 *  ACT V  – LARGE NUMERAL DATE: counter lerps + clip-path wipe + 4-corner
 *           ornaments expand to corners
 *  ACT VI – RSVP card with magnetic 3D tilt on cursor
 */
export function PrismLayout({ formSlot, program, title, dateTime, location, description, galleryImages }: JoinLayoutProps) {
  const lenisRef = useRef<Lenis | null>(null);
  const cubeRef = useRef<HTMLDivElement>(null);
  const tiltCardRef = useRef<HTMLDivElement>(null);
  const videoSectionRef = useRef<HTMLElement | null>(null);
  const videoCanvasRef = useRef<HTMLCanvasElement | null>(null);

  // Act III video-frame scroll → Apple-style frame sequence (no <video> scrub).
  useScrollFrameSequence({
    containerRef: videoSectionRef,
    canvasRef: videoCanvasRef,
    source: { kind: "video", url: DEMO_VIDEOS.timelineScroll, count: DEMO_FRAME_COUNT },
    pinViewports: 3,
  });

  const d = dateTime ? new Date(dateTime) : null;
  const day = d?.getDate().toString().padStart(2, "0") ?? "--";
  const monthShort = d?.toLocaleString("en-US", { month: "short" }).toUpperCase() ?? "—";
  const year = d?.getFullYear() ?? 2026;
  const fullDate = d ? `${day}.${(d.getMonth() + 1).toString().padStart(2, "0")}.${year}` : "—";

  // ── Magnetic tilt on form card ───────────────────────────────────
  useEffect(() => {
    const card = tiltCardRef.current;
    if (!card) return;
    const handleMove = (e: MouseEvent) => {
      const r = card.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const dx = (e.clientX - cx) / r.width;
      const dy = (e.clientY - cy) / r.height;
      card.style.transform = `perspective(1200px) rotateY(${dx * 8}deg) rotateX(${-dy * 8}deg)`;
    };
    const reset = () => { card.style.transform = "perspective(1200px) rotateY(0) rotateX(0)"; };
    window.addEventListener("mousemove", handleMove);
    card.addEventListener("mouseleave", reset);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      card.removeEventListener("mouseleave", reset);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = createLenis({ duration: 1.4, easing: (t) => 1 - Math.pow(1 - t, 5) });
    lenis.scrollTo(0, { immediate: true });
    lenisRef.current = lenis;
    const ticker = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(ticker);
    gsap.ticker.lagSmoothing(0);
    lenis.on("scroll", ScrollTrigger.update);

    const ctx = gsap.context(() => {
      // ── ACT I: HERO entrance ────────────────────────────────────────
      const tl = gsap.timeline({ defaults: { ease: "expo.out" } });

      // Marquees slide in opposite directions
      tl.from(".prm-marquee-top", { x: "100%", duration: 1.6 }, 0);
      tl.from(".prm-marquee-bot", { x: "-100%", duration: 1.6 }, 0);

      // 4 satellite cards from 4 corners
      tl.from(".prm-sat-tl", { x: -100, y: -100, opacity: 0, rotate: -25, duration: 1.4 }, 0.4);
      tl.from(".prm-sat-tr", { x:  100, y: -100, opacity: 0, rotate:  25, duration: 1.4 }, 0.5);
      tl.from(".prm-sat-bl", { x: -100, y:  100, opacity: 0, rotate:  25, duration: 1.4 }, 0.6);
      tl.from(".prm-sat-br", { x:  100, y:  100, opacity: 0, rotate: -25, duration: 1.4 }, 0.7);

      // 3D extruded title — characters drop in with rotateX
      const heroSplit = new SplitText(".prm-hero-title", { type: "chars" });
      tl.from(heroSplit.chars, {
        y: -120,
        rotateX: -90,
        opacity: 0,
        stagger: { each: 0.06, from: "edges" },
        duration: 1.2,
      }, 0.6);

      tl.from(".prm-hero-meta", { y: 20, opacity: 0, stagger: 0.1, duration: 0.9 }, 1.2);

      // Continuous orbit on satellites (subtle)
      gsap.to(".prm-sat", { y: "+=20", rotate: "+=8", duration: 4, ease: "sine.inOut", repeat: -1, yoyo: true, stagger: 0.3 });

      // ── ACT II: PINNED 3D CUBE ─────────────────────────────────────
      const cube = cubeRef.current;
      if (cube) {
        const cubeTl = gsap.timeline({
          scrollTrigger: {
            trigger: ".prm-cube-section",
            start: "top top",
            end: "+=400%",
            pin: true,
            scrub: 1,
          },
        });

        // Cube rotates Y 360° + X 180° + scale 0.8→1.2→0.8
        cubeTl.to(cube, { rotateY: 360, rotateX: 180, ease: "none" }, 0);
        cubeTl.fromTo(cube, { scale: 0.85 }, { scale: 1.1, ease: "sine.inOut" }, 0);
        cubeTl.to(cube, { scale: 0.85, ease: "sine.inOut" }, 0.5);

        // Satellite cards orbit at 1.5x speed
        gsap.to(".prm-orbit-card", {
          rotate: 540,
          ease: "none",
          scrollTrigger: {
            trigger: ".prm-cube-section",
            start: "top top",
            end: "+=400%",
            scrub: 1,
          },
        });

        // Stage indicator updates with cube face
        const indicator = document.querySelector<HTMLElement>(".prm-cube-stage");
        const labels = ["I · TITLE", "II · DATE", "III · PLACE", "IV · STORY", "V · GALLERY", "VI · RSVP"];
        if (indicator) {
          ScrollTrigger.create({
            trigger: ".prm-cube-section",
            start: "top top",
            end: "+=400%",
            onUpdate(self) {
              const stage = Math.floor(self.progress * 6);
              indicator.textContent = labels[Math.min(stage, 5)] ?? labels[5];
            },
          });
        }

        // Light sweep across cube
        gsap.fromTo(".prm-cube-light", { x: "-100%" }, {
          x: "100%",
          ease: "none",
          scrollTrigger: { trigger: ".prm-cube-section", start: "top top", end: "+=400%", scrub: 1 },
        });
      }

      // ── ACT III: FRAME SCROLL — pin + canvas handled by hook ─────
      const vidSection = document.querySelector<HTMLElement>(".prm-video-section");
      if (vidSection) {
        // Tickers + image fragments sync off the same 3-viewport pin.
        gsap.fromTo(".prm-ticker-1", { x: "-100%" }, { x: "100%", ease: "none",
          scrollTrigger: { trigger: vidSection, start: "top top", end: "+=300%", scrub: 1 } });
        gsap.fromTo(".prm-ticker-2", { x: "100%" }, { x: "-100%", ease: "none",
          scrollTrigger: { trigger: vidSection, start: "top top", end: "+=300%", scrub: 1 } });

        gsap.from(".prm-frag-tl", { x: -100, y: -100, opacity: 0, duration: 1, ease: "back.out(1.5)",
          scrollTrigger: { trigger: vidSection, start: "top top+=10%", toggleActions: "play reverse play reverse" } });
        gsap.from(".prm-frag-tr", { x: 100, y: -100, opacity: 0, duration: 1, ease: "back.out(1.5)",
          scrollTrigger: { trigger: vidSection, start: "top top+=30%", toggleActions: "play reverse play reverse" } });
        gsap.from(".prm-frag-bl", { x: -100, y: 100, opacity: 0, duration: 1, ease: "back.out(1.5)",
          scrollTrigger: { trigger: vidSection, start: "top top+=50%", toggleActions: "play reverse play reverse" } });
        gsap.from(".prm-frag-br", { x: 100, y: 100, opacity: 0, duration: 1, ease: "back.out(1.5)",
          scrollTrigger: { trigger: vidSection, start: "top top+=70%", toggleActions: "play reverse play reverse" } });
      }

      // ── ACT IV: ORBIT GALLERY (each card on parabolic path) ────────
      document.querySelectorAll(".prm-arc-card").forEach((card, i) => {
        const dir = i % 2 === 0 ? 1 : -1;
        gsap.fromTo(card,
          { x: -200 * dir, y: 100, rotate: -20 * dir, opacity: 0 },
          {
            x: 200 * dir, y: -100, rotate: 20 * dir, opacity: 1,
            ease: "power2.inOut",
            scrollTrigger: { trigger: ".prm-orbit-section", start: "top bottom", end: "bottom top", scrub: 1.5 },
          }
        );
      });

      gsap.from(".prm-orbit-title", { y: 60, opacity: 0, duration: 1.2, ease: "expo.out",
        scrollTrigger: { trigger: ".prm-orbit-section", start: "top 70%", toggleActions: "play reverse play reverse" } });

      // ── ACT V: NUMERAL DATE ────────────────────────────────────────
      const dayEl = document.querySelector<HTMLElement>(".prm-num-day");
      const monthEl = document.querySelector<HTMLElement>(".prm-num-month");
      const yearEl = document.querySelector<HTMLElement>(".prm-num-year");
      if (dayEl && monthEl && yearEl && d) {
        const dayTarget = d.getDate();
        const monthTarget = d.getMonth() + 1;
        const yearTarget = d.getFullYear();
        const obj = { day: 0, month: 0, year: 0 };
        gsap.to(obj, {
          day: dayTarget, month: monthTarget, year: yearTarget,
          duration: 1.5, ease: "power3.out",
          scrollTrigger: { trigger: ".prm-date-section", start: "top 70%", toggleActions: "play reverse play reverse" },
          onUpdate() {
            dayEl.textContent = Math.round(obj.day).toString().padStart(2, "0");
            monthEl.textContent = Math.round(obj.month).toString().padStart(2, "0");
            yearEl.textContent = Math.round(obj.year).toString();
          },
        });
      }

      gsap.from(".prm-date-corner", {
        scale: 0,
        x: (i) => [-50, 50, -50, 50][i],
        y: (i) => [-50, -50, 50, 50][i],
        opacity: 0,
        duration: 1.2,
        ease: "back.out(1.5)",
        scrollTrigger: { trigger: ".prm-date-section", start: "top 65%", toggleActions: "play reverse play reverse" },
      });

      gsap.from(".prm-date-line", { scaleX: 0, transformOrigin: (i) => i === 0 ? "left" : "right", duration: 1.2, ease: "expo.inOut",
        scrollTrigger: { trigger: ".prm-date-section", start: "top 65%", toggleActions: "play reverse play reverse" } });

      // ── ACT VI: RSVP card ──────────────────────────────────────────
      gsap.from(".prm-form-card", { opacity: 0, y: 60, rotateX: -20, duration: 1.4, ease: "expo.out",
        scrollTrigger: { trigger: ".prm-form-card", start: "top 80%", toggleActions: "play reverse play reverse" } });
    });

    return () => {
      lenis.destroy();
      gsap.ticker.remove(ticker);
      ScrollTrigger.killAll();
      ctx.revert();
    };
  }, []);

  // Cube face dimensions (responsive)
  const CUBE_SIZE = "min(50vw, 24rem)";

  return (
    <main className="relative overflow-x-hidden text-white" style={{ background: "#06080f", fontFamily: "'Inter', system-ui, sans-serif" }}>
      <style>{`
        @keyframes prmMarqueeR { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes prmMarqueeL { from { transform: translateX(-50%); } to { transform: translateX(0); } }
      `}</style>

      {/* ═══ BACKGROUND GRADIENT ════════════════════════════════════ */}
      <div className="fixed inset-0 -z-30 pointer-events-none" style={{
        background: "radial-gradient(ellipse at 20% 10%, #0e1a3a 0%, transparent 50%), radial-gradient(ellipse at 80% 90%, #3a0e1e 0%, transparent 50%), #06080f",
      }} />

      {/* ═══ ACT I: HERO ═══════════════════════════════════════════════ */}
      <section className="relative min-h-screen flex flex-col justify-between overflow-hidden">
        {/* Top marquee — left direction */}
        <div className="prm-marquee-top relative h-12 border-b border-white/10 overflow-hidden flex items-center">
          <div className="whitespace-nowrap text-[10px] tracking-[0.6em] uppercase text-white/70" style={{ animation: "prmMarqueeR 28s linear infinite" }}>
            {Array(10).fill(null).map((_, i) => (
              <span key={i} className="mx-6">
                {title.toUpperCase()} <span style={{ color: "#ff6b35" }}>◆</span> ACT {year} <span style={{ color: "#ff6b35" }}>◆</span> {fullDate} <span style={{ color: "#ff6b35" }}>◆</span>
              </span>
            ))}
          </div>
        </div>

        {/* Center stage */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 relative">
          {/* Satellite cards in 4 corners */}
          <div className="prm-sat prm-sat-tl absolute top-12 left-12 w-32 h-44 hidden md:block" style={{ background: `url(${galleryImages[0]}) center/cover`, transform: "rotate(-6deg)", boxShadow: "0 20px 40px rgba(0,0,0,0.5)" }}>
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 60%, rgba(0,0,0,0.5))" }} />
            <span className="absolute bottom-2 left-2 text-[9px] tracking-[0.3em] uppercase">01</span>
          </div>
          <div className="prm-sat prm-sat-tr absolute top-16 right-12 w-32 h-44 hidden md:block" style={{ background: `url(${galleryImages[1]}) center/cover`, transform: "rotate(6deg)", boxShadow: "0 20px 40px rgba(0,0,0,0.5)" }}>
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 60%, rgba(0,0,0,0.5))" }} />
            <span className="absolute bottom-2 left-2 text-[9px] tracking-[0.3em] uppercase">02</span>
          </div>
          <div className="prm-sat prm-sat-bl absolute bottom-20 left-16 w-32 h-44 hidden md:block" style={{ background: `url(${galleryImages[2]}) center/cover`, transform: "rotate(-3deg)", boxShadow: "0 20px 40px rgba(0,0,0,0.5)" }}>
            <span className="absolute bottom-2 left-2 text-[9px] tracking-[0.3em] uppercase">03</span>
          </div>
          <div className="prm-sat prm-sat-br absolute bottom-24 right-16 w-32 h-44 hidden md:block" style={{ background: `url(${galleryImages[3]}) center/cover`, transform: "rotate(8deg)", boxShadow: "0 20px 40px rgba(0,0,0,0.5)" }}>
            <span className="absolute bottom-2 left-2 text-[9px] tracking-[0.3em] uppercase">04</span>
          </div>

          {/* Title */}
          <p className="prm-hero-meta text-[10px] uppercase tracking-[0.6em] text-white/40 mb-6">A 6-ACT SPECTACLE</p>

          <h1 className="prm-hero-title text-center font-light leading-[0.85] tracking-[-0.04em] relative" style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: "clamp(4rem, 13vw, 12rem)",
          }}>
            {/* 3D extruded effect via stacked text-shadows */}
            <span style={{
              textShadow: "1px 1px 0 #ff6b35, 2px 2px 0 #ff6b35, 3px 3px 0 rgba(255,107,53,0.6), 4px 4px 0 rgba(255,107,53,0.4), 5px 5px 0 rgba(255,107,53,0.2), 0 0 60px rgba(255,107,53,0.2)",
            }}>{title}</span>
          </h1>

          <div className="prm-hero-meta mt-12 flex items-center gap-6 text-xs uppercase tracking-[0.4em] text-white/60">
            <span>{monthShort}</span>
            <span style={{ color: "#ff6b35" }}>◆</span>
            <span className="tabular-nums">{day}</span>
            <span style={{ color: "#ff6b35" }}>◆</span>
            <span>{year}</span>
          </div>

          <p className="prm-hero-meta mt-4 italic text-white/40" style={{ fontFamily: "'Playfair Display', serif", fontSize: 18 }}>
            {location?.split(",")[0] ?? "—"}
          </p>
        </div>

        {/* Bottom marquee — right direction */}
        <div className="prm-marquee-bot relative h-12 border-t border-white/10 overflow-hidden flex items-center">
          <div className="whitespace-nowrap text-[10px] tracking-[0.6em] uppercase text-[#ff6b35]/70" style={{ animation: "prmMarqueeL 32s linear infinite" }}>
            {Array(10).fill(null).map((_, i) => (
              <span key={i} className="mx-6">
                ◇ BLACK-TIE ◇ {(location ?? "").toUpperCase()} ◇ RSVP REQUESTED ◇
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ ACT II: PINNED 3D CUBE ═══════════════════════════════════ */}
      <section className="prm-cube-section relative h-screen overflow-hidden">
        <div className="prm-cube-stage absolute top-12 right-12 z-30 text-[10px] uppercase tracking-[0.5em]" style={{ color: "#ff6b35" }}>
          I · TITLE
        </div>
        <div className="absolute top-12 left-12 z-30 text-[10px] uppercase tracking-[0.5em] text-white/40">
          THE PRISM · SCROLL
        </div>

        {/* Light sweep overlay */}
        <div className="prm-cube-light absolute top-0 left-0 w-1/2 h-full pointer-events-none z-20" style={{
          background: "linear-gradient(90deg, transparent 0%, rgba(255,107,53,0.08) 50%, transparent 100%)",
          filter: "blur(40px)",
        }} />

        {/* Stage with cube */}
        <div className="absolute inset-0 flex items-center justify-center" style={{ perspective: 1600 }}>
          <div ref={cubeRef} className="relative" style={{
            width: CUBE_SIZE, height: CUBE_SIZE,
            transformStyle: "preserve-3d",
            transform: "rotateY(0) rotateX(0)",
          }}>
            {[
              { face: "front", transform: `translateZ(calc(${CUBE_SIZE} / 2))`, label: "I · TITLE", main: title, sub: "A 6-act invitation" },
              { face: "right", transform: `rotateY(90deg) translateZ(calc(${CUBE_SIZE} / 2))`, label: "II · DATE", main: fullDate, sub: monthShort + " · " + year },
              { face: "back", transform: `rotateY(180deg) translateZ(calc(${CUBE_SIZE} / 2))`, label: "III · PLACE", main: location?.split(",")[0] ?? "—", sub: location?.split(",").slice(1).join(",").trim() ?? "" },
              { face: "left", transform: `rotateY(-90deg) translateZ(calc(${CUBE_SIZE} / 2))`, label: "IV · STORY", main: "Bir An,", sub: "bir ömür" },
              { face: "top", transform: `rotateX(90deg) translateZ(calc(${CUBE_SIZE} / 2))`, label: "V · GALLERY", main: "5", sub: "memories" },
              { face: "bottom", transform: `rotateX(-90deg) translateZ(calc(${CUBE_SIZE} / 2))`, label: "VI · RSVP", main: "Join", sub: "us." },
            ].map((f, i) => (
              <div key={i} className="absolute inset-0 flex flex-col items-center justify-center text-center p-8" style={{
                transform: f.transform,
                background: "linear-gradient(180deg, #0a0f1f 0%, #050810 100%)",
                border: "1px solid rgba(255, 107, 53, 0.3)",
                boxShadow: "inset 0 0 60px rgba(255, 107, 53, 0.1), 0 30px 60px rgba(0,0,0,0.6)",
              }}>
                <p className="text-[10px] uppercase tracking-[0.5em] mb-6" style={{ color: "#ff6b35" }}>{f.label}</p>
                <p className="font-light leading-none mb-3" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 5vw, 4rem)" }}>{f.main}</p>
                <p className="text-sm italic text-white/60" style={{ fontFamily: "'Playfair Display', serif" }}>{f.sub}</p>
                <div className="absolute top-3 left-3 w-6 h-6 border-t border-l" style={{ borderColor: "#ff6b35" }} />
                <div className="absolute top-3 right-3 w-6 h-6 border-t border-r" style={{ borderColor: "#ff6b35" }} />
                <div className="absolute bottom-3 left-3 w-6 h-6 border-b border-l" style={{ borderColor: "#ff6b35" }} />
                <div className="absolute bottom-3 right-3 w-6 h-6 border-b border-r" style={{ borderColor: "#ff6b35" }} />
              </div>
            ))}
          </div>
        </div>

        {/* Orbiting satellite cards (around cube) */}
        <div className="absolute inset-0 pointer-events-none" style={{ perspective: 2000 }}>
          <div className="prm-orbit-card absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" style={{ width: 1, height: 1 }}>
            <div className="absolute" style={{ left: "20vw", top: 0, width: 100, height: 140, background: `url(${galleryImages[0]}) center/cover`, transform: "translate(-50%, -50%) rotate(-15deg)", boxShadow: "0 16px 32px rgba(0,0,0,0.5)" }} />
            <div className="absolute" style={{ left: "-20vw", top: 0, width: 100, height: 140, background: `url(${galleryImages[2]}) center/cover`, transform: "translate(-50%, -50%) rotate(15deg)", boxShadow: "0 16px 32px rgba(0,0,0,0.5)" }} />
            <div className="absolute" style={{ left: 0, top: "20vh", width: 100, height: 140, background: `url(${galleryImages[3]}) center/cover`, transform: "translate(-50%, -50%) rotate(8deg)", boxShadow: "0 16px 32px rgba(0,0,0,0.5)" }} />
          </div>
        </div>
      </section>

      {/* ═══ ACT III: FRAME SCROLL ═══════════════════════════════════ */}
      <section
        ref={videoSectionRef as React.RefObject<HTMLElement>}
        className="prm-video-section relative h-screen overflow-hidden"
      >
        <canvas
          ref={videoCanvasRef}
          className="prm-video-canvas absolute inset-0 w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-black/70" />

        {/* Multi-directional tickers */}
        <div className="prm-ticker-1 absolute top-[20%] left-0 right-0 whitespace-nowrap text-7xl font-black uppercase tracking-tighter pointer-events-none" style={{ fontFamily: "'Playfair Display', serif", color: "#ff6b35", opacity: 0.4, fontSize: "clamp(3rem, 8vw, 7rem)" }}>
          {Array(5).fill(`${title} · `).join("")}
        </div>
        <div className="prm-ticker-2 absolute bottom-[20%] left-0 right-0 whitespace-nowrap text-right text-7xl font-black uppercase tracking-tighter pointer-events-none" style={{ fontFamily: "'Playfair Display', serif", color: "white", opacity: 0.7, fontSize: "clamp(3rem, 8vw, 7rem)" }}>
          {Array(5).fill(`${monthShort} ${day} ${year} · `).join("")}
        </div>

        {/* Center tagline */}
        <div className="relative z-10 h-full flex items-center justify-center px-6">
          <div className="text-center">
            <p className="text-[10px] uppercase tracking-[0.6em] mb-6" style={{ color: "#ff6b35" }}>ACT III · MONTAGE</p>
            <p className="font-light italic leading-tight" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3.5rem)" }}>
              "İki kalp, sonsuz bir kare."
            </p>
          </div>
        </div>

        {/* Corner image fragments */}
        <div className="prm-frag-tl absolute top-12 left-12 w-32 h-44 hidden md:block" style={{ background: `url(${galleryImages[0]}) center/cover`, transform: "rotate(-8deg)", boxShadow: "0 20px 40px rgba(0,0,0,0.6)" }} />
        <div className="prm-frag-tr absolute top-12 right-12 w-32 h-44 hidden md:block" style={{ background: `url(${galleryImages[1]}) center/cover`, transform: "rotate(8deg)", boxShadow: "0 20px 40px rgba(0,0,0,0.6)" }} />
        <div className="prm-frag-bl absolute bottom-12 left-12 w-32 h-44 hidden md:block" style={{ background: `url(${galleryImages[2]}) center/cover`, transform: "rotate(8deg)", boxShadow: "0 20px 40px rgba(0,0,0,0.6)" }} />
        <div className="prm-frag-br absolute bottom-12 right-12 w-32 h-44 hidden md:block" style={{ background: `url(${galleryImages[3]}) center/cover`, transform: "rotate(-8deg)", boxShadow: "0 20px 40px rgba(0,0,0,0.6)" }} />
      </section>

      {/* ═══ ACT IV: ORBIT GALLERY ═════════════════════════════════════ */}
      <section className="prm-orbit-section relative h-screen overflow-hidden flex items-center justify-center">
        <div className="prm-orbit-title relative z-10 text-center pointer-events-none">
          <p className="text-[10px] uppercase tracking-[0.6em] mb-4" style={{ color: "#ff6b35" }}>ACT IV · GALLERY</p>
          <h2 className="font-light leading-none tracking-tight" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(3rem, 8vw, 7rem)" }}>
            Five Memories
          </h2>
        </div>

        {/* Arc cards traverse */}
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="prm-arc-card absolute w-44 h-60 sm:w-56 sm:h-72"
            style={{
              background: `url(${galleryImages[i]}) center/cover`,
              top: `${20 + i * 10}%`,
              left: `${10 + i * 18}%`,
              boxShadow: "0 30px 60px rgba(0,0,0,0.6)",
              border: "1px solid rgba(255,107,53,0.2)",
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            <span className="absolute bottom-3 left-3 text-[10px] tracking-[0.4em] uppercase" style={{ color: "#ff6b35" }}>0{i + 1}</span>
          </div>
        ))}
      </section>

      {/* ═══ ACT V: NUMERAL DATE ═══════════════════════════════════════ */}
      <section className="prm-date-section relative min-h-screen flex items-center justify-center px-6 py-32 overflow-hidden">
        {/* 4-corner ornaments */}
        <div className="prm-date-corner absolute top-12 left-12 w-16 h-16 border-t-2 border-l-2" style={{ borderColor: "#ff6b35" }} />
        <div className="prm-date-corner absolute top-12 right-12 w-16 h-16 border-t-2 border-r-2" style={{ borderColor: "#ff6b35" }} />
        <div className="prm-date-corner absolute bottom-12 left-12 w-16 h-16 border-b-2 border-l-2" style={{ borderColor: "#ff6b35" }} />
        <div className="prm-date-corner absolute bottom-12 right-12 w-16 h-16 border-b-2 border-r-2" style={{ borderColor: "#ff6b35" }} />

        <div className="text-center">
          <p className="text-[10px] uppercase tracking-[0.6em] mb-12" style={{ color: "#ff6b35" }}>ACT V · THE DATE</p>

          <div className="flex items-center justify-center gap-4 sm:gap-8">
            <div>
              <p className="prm-num-day font-light tabular-nums leading-none tracking-[-0.05em]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(6rem, 18vw, 16rem)" }}>00</p>
              <p className="text-[10px] uppercase tracking-[0.4em] text-white/40 mt-3">Day</p>
            </div>
            <div className="text-white/20 font-light leading-none" style={{ fontSize: "clamp(4rem, 10vw, 9rem)", fontFamily: "'Playfair Display', serif" }}>·</div>
            <div>
              <p className="prm-num-month font-light tabular-nums leading-none tracking-[-0.05em]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(6rem, 18vw, 16rem)" }}>00</p>
              <p className="text-[10px] uppercase tracking-[0.4em] text-white/40 mt-3">Month</p>
            </div>
            <div className="text-white/20 font-light leading-none" style={{ fontSize: "clamp(4rem, 10vw, 9rem)", fontFamily: "'Playfair Display', serif" }}>·</div>
            <div>
              <p className="prm-num-year font-light tabular-nums leading-none tracking-[-0.05em]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(4rem, 13vw, 11rem)", color: "#ff6b35" }}>0000</p>
              <p className="text-[10px] uppercase tracking-[0.4em] text-white/40 mt-3">Year</p>
            </div>
          </div>

          <div className="flex items-center justify-center gap-4 mt-16">
            <div className="prm-date-line h-px w-32" style={{ background: "linear-gradient(90deg, transparent, #ff6b35)" }} />
            <p className="text-xs tracking-[0.5em] uppercase text-white/70">{d?.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false })}</p>
            <div className="prm-date-line h-px w-32" style={{ background: "linear-gradient(-90deg, transparent, #ff6b35)" }} />
          </div>
        </div>
      </section>

      {/* ═══ ACT VI: RSVP with magnetic 3D tilt ═══════════════════════ */}
      <section className="relative min-h-screen flex items-center justify-center px-6 py-32" style={{ perspective: 1200 }}>
        <div
          ref={tiltCardRef}
          className="prm-form-card w-full max-w-md p-12 relative transition-transform duration-300 ease-out"
          style={{
            background: "linear-gradient(135deg, rgba(255, 107, 53, 0.08) 0%, rgba(255, 107, 53, 0.02) 100%)",
            border: "1px solid rgba(255, 107, 53, 0.3)",
            boxShadow: "inset 0 1px 0 rgba(255, 107, 53, 0.2), 0 40px 100px rgba(0, 0, 0, 0.7)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            transformStyle: "preserve-3d",
          }}
        >
          {/* 4-corner accents */}
          <div className="absolute top-3 left-3 w-5 h-5 border-t border-l" style={{ borderColor: "#ff6b35" }} />
          <div className="absolute top-3 right-3 w-5 h-5 border-t border-r" style={{ borderColor: "#ff6b35" }} />
          <div className="absolute bottom-3 left-3 w-5 h-5 border-b border-l" style={{ borderColor: "#ff6b35" }} />
          <div className="absolute bottom-3 right-3 w-5 h-5 border-b border-r" style={{ borderColor: "#ff6b35" }} />

          <p className="text-center text-[10px] uppercase tracking-[0.6em] mb-4" style={{ color: "#ff6b35" }}>ACT VI · RSVP</p>
          <h3 className="text-center font-light italic mb-3" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3rem)" }}>
            Final Act
          </h3>
          <div className="mx-auto w-12 h-px mb-10" style={{ background: "#ff6b35" }} />

          <WeddingProgram entries={program} className="mb-10" />
          {formSlot}

          <p className="text-center text-[10px] tracking-[0.4em] uppercase text-white/30 mt-8">
            ↑ Move cursor to feel the tilt ↑
          </p>
        </div>
      </section>
    </main>
  );
}
